import { useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Phone, ArrowUpRight, ArrowLeft, ShieldCheck, Eye, Award } from "lucide-react";
import { SmoothScroll, Reveal, MaskReveal } from "@/components/site/motion";
import Navbar from "@/components/site/Navbar";
import Services from "@/components/site/Services";
import Gallery from "@/components/site/Gallery";
import FAQ from "@/components/site/FAQ";
import Contact from "@/components/site/Contact";
import Footer from "@/components/site/Footer";
import StickyButtons from "@/components/site/StickyButtons";
import SEO from "@/components/site/SEO";
import { localityBySlug, LOCALITIES } from "@/lib/localities";
import { BUSINESS } from "@/lib/api";
import { trackConversion } from "@/lib/gtag";

const HERO_IMG =
  "https://customer-assets-v7afamib.emergentagent.net/job_grillpro-hyd/artifacts/y0jl471p_IMG-20251124-WA0100.webp";

export default function LocalityPage() {
  const { slug } = useParams();
  const nav = useNavigate();
  const locality = localityBySlug(slug);
  const contactRef = useRef(null);
  const scrollToQuote = () => contactRef.current?.scrollIntoView({ behavior: "smooth" });

  if (!locality) {
    return (
      <div className="min-h-screen bg-steel-900 text-white flex flex-col items-center justify-center gap-6 px-6 text-center">
        <h1 className="font-display font-black text-4xl">Area not found</h1>
        <button onClick={() => nav("/")} className="bg-white text-steel-900 px-6 py-3 font-semibold">Back to home</button>
      </div>
    );
  }

  const title = `${locality.headline} | ${BUSINESS.name} — Free Quote`;
  const description = `${locality.name} invisible grills for balconies & windows. Child & pet safe SS304/SS316 steel cables, unobstructed views, 10-yr warranty. Free quote — call ${BUSINESS.phoneDisplay}.`;
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    name: `${BUSINESS.name} — ${locality.name}`,
    telephone: `+91${BUSINESS.phone}`,
    areaServed: { "@type": "Place", name: `${locality.name}, Hyderabad` },
    address: { "@type": "PostalAddress", addressLocality: locality.name, addressRegion: "Telangana", addressCountry: "IN" },
    description,
  };

  const nearby = locality.nearby.map(localityBySlug).filter(Boolean);
  const trust = [
    { icon: ShieldCheck, label: "Child & pet safe" },
    { icon: Eye, label: "View preserved" },
    { icon: Award, label: "10-year warranty" },
  ];

  return (
    <SmoothScroll>
      <SEO title={title} description={description} path={`/areas/${locality.slug}`} jsonLd={jsonLd} />
      <div data-testid={`locality-page-${locality.slug}`}>
        <Navbar />

        <main>
        {/* Locality hero */}
        <section className="relative min-h-[92vh] pt-[92px] overflow-hidden bg-white">
          <div className="max-w-[1400px] mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-8 items-center min-h-[calc(92vh-92px)]">
            <div className="lg:col-span-6 relative z-10 pt-8 lg:pt-0">
              <Link to="/" data-testid="locality-back-home" className="inline-flex items-center gap-2 text-steel-500 hover:text-steel-900 transition-colors mb-6 text-sm font-label">
                <ArrowLeft size={15} /> Home
              </Link>
              <p className="font-label text-xs tracking-[0.3em] uppercase text-steel-600 mb-5 flex items-center gap-3">
                <span className="h-px w-10 bg-steel-400" /> Hyderabad · {locality.name}
              </p>
              <h1 className="font-display font-black text-steel-900 tracking-tighter leading-[0.92] text-4xl md:text-6xl lg:text-[4.6rem]">
                <MaskReveal lines={["Invisible Grills", `in ${locality.name}`]} delay={0.1} />
              </h1>
              <motion.p
                initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }}
                className="mt-7 text-base md:text-lg text-steel-600 max-w-lg leading-relaxed"
              >
                {locality.intro}
              </motion.p>
              <motion.div
                initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.75 }}
                className="mt-8 flex flex-col sm:flex-row gap-4"
              >
                <button onClick={scrollToQuote} data-testid="locality-quote-btn" className="bg-steel-800 text-white px-8 py-4 font-semibold hover:bg-steel-900 transition-colors">
                  Get a Free Quote in {locality.name}
                </button>
                <a href={`tel:+91${BUSINESS.phone}`} onClick={trackConversion} className="border border-steel-300 text-steel-900 px-8 py-4 font-semibold flex items-center justify-center gap-2 hover:bg-steel-50 transition-colors">
                  <Phone size={18} /> {BUSINESS.phoneDisplay}
                </a>
              </motion.div>
              <motion.div
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.95 }}
                className="mt-9 flex flex-wrap gap-x-8 gap-y-3"
              >
                {trust.map((t) => (
                  <div key={t.label} className="flex items-center gap-2 text-steel-700">
                    <t.icon size={18} className="text-steel-800" strokeWidth={1.6} /> <span className="text-sm font-label">{t.label}</span>
                  </div>
                ))}
              </motion.div>
            </div>
            <div className="lg:col-span-6 relative h-[46vh] lg:h-[74vh]">
              <motion.div
                initial={{ clipPath: "inset(100% 0 0 0)" }} animate={{ clipPath: "inset(0% 0 0 0)" }}
                transition={{ duration: 1, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
                className="absolute inset-0 overflow-hidden clip-slant border border-steel-200"
              >
                <img src={HERO_IMG} alt={`Invisible grills installation in ${locality.name}, Hyderabad`} width={1200} height={1500} fetchPriority="high" decoding="async" className="w-full h-full object-cover" />
                <div className="absolute inset-0 grill-lines pointer-events-none" />
              </motion.div>
            </div>
          </div>
        </section>

        {/* Landmarks / local relevance */}
        <section className="py-20 md:py-28 bg-steel-50 border-y border-steel-200">
          <div className="max-w-[1400px] mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <Reveal>
                <p className="font-label text-xs tracking-[0.3em] uppercase text-steel-500 mb-4">Serving your neighbourhood</p>
                <h2 className="font-display font-black tracking-tighter text-steel-900 text-3xl md:text-5xl leading-none">
                  Trusted around {locality.name}.
                </h2>
              </Reveal>
            </div>
            <div className="lg:col-span-7">
              <Reveal delay={0.1}>
                <p className="text-steel-600 leading-relaxed mb-6">
                  We regularly install invisible grills for apartments and villas near {locality.name}&apos;s landmarks and communities:
                </p>
                <div className="flex flex-wrap gap-3">
                  {locality.landmarks.map((l) => (
                    <span key={l} className="border border-steel-300 px-5 py-2.5 font-label text-sm text-steel-800 bg-white">{l}</span>
                  ))}
                </div>
              </Reveal>
            </div>
          </div>
        </section>

        <Services onQuote={scrollToQuote} />
        <Gallery />

        {/* Nearby areas — internal linking for SEO */}
        <section className="py-20 md:py-28 bg-white border-t border-steel-200">
          <div className="max-w-[1400px] mx-auto px-5 md:px-10">
            <Reveal className="mb-10">
              <p className="font-label text-xs tracking-[0.3em] uppercase text-steel-500 mb-4">Also serving nearby</p>
              <h2 className="font-display font-black tracking-tighter text-steel-900 text-3xl md:text-5xl leading-none">
                Invisible grills across Hyderabad
              </h2>
            </Reveal>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="nearby-areas">
              {nearby.map((n, i) => (
                <Reveal key={n.slug} delay={i * 0.06}>
                  <Link
                    to={`/areas/${n.slug}`}
                    data-testid={`nearby-${n.slug}`}
                    className="group flex items-center justify-between border border-steel-200 p-6 hover:bg-steel-800 transition-colors"
                  >
                    <div>
                      <div className="font-display font-bold text-lg text-steel-900 group-hover:text-white transition-colors">{n.name}</div>
                      <div className="font-label text-xs uppercase tracking-wider text-steel-500 group-hover:text-steel-300">Invisible grills</div>
                    </div>
                    <ArrowUpRight className="text-steel-400 group-hover:text-white transition-colors" size={20} />
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        <FAQ />
        <Contact ref={contactRef} defaultLocation={locality.name} />
        </main>
        <Footer />
        <StickyButtons />
      </div>
    </SmoothScroll>
  );
}

export { LOCALITIES };
